/**
 * Générateur — O8 : reconnaître sur un graphique une situation de
 * proportionnalité ou de non-proportionnalité.
 * Attendu ID : 171 | Niveau : 4ème | Domaine : proportionnalite
 *
 * Critère canonique (« Exemples de réussite ») : les points sont ALIGNÉS AVEC
 * L'ORIGINE ⟺ proportionnalité. Quatre natures de série :
 *   - « prop »           : points alignés avec l'origine → proportionnelle
 *   - « affine »         : points alignés, mais pas avec l'origine → non
 *   - « courbe_origine » : points NON alignés, sur une courbe qui part de
 *                          l'origine (y = c·x², du type 1, 4, 9, 16) → non
 *   - « courbe »         : points NON alignés, croissants, courbe qui ne passe
 *                          pas par l'origine → non
 *
 * Figure : svgRepere (proportionnalite.js), séries de quatre points reliés
 * entre eux, jamais prolongés vers l'origine, aucune droite tracée par O, aucun
 * mot sur la figure ; `alt` neutre qui ne dit jamais la nature.
 *
 * Revue 2026-09-23 (vague 3), décisions du coordinateur appliquées en mode
 * auto. Attendu de fin de 4e (Eduscol, p. 6) : « Il reconnaît sur un graphique
 * une situation de proportionnalité ou de non proportionnalité. » Exemple de
 * réussite : « À partir d'un graphique, il traduit l'alignement des points avec
 * l'origine par une situation de proportionnalité. » Cours (19Proport1 p. 2) :
 * « Sur un graphique, une situation de proportionnalité est représentée par des
 * points alignés avec l'origine. »
 *   - Séries : coefficients décimaux (0,8 ; 1,2 ; 1,5 ; 2 ; 2,5), abscisses de
 *     1 à 10, ordonnées de 1 à 24. Les ordonnées sont tenues en CENTIÈMES
 *     entiers : la nature déclarée par la fabrique est revérifiée par un second
 *     chemin exact (produits en croix entiers), throw sinon.
 *   - « VISIBLEMENT » non alignées : un point au moins s'écarte de la corde de
 *     la série d'un carreau (un pas de graduation) ou plus ; la série 2, 5, 9,
 *     14 presque alignée (9,9 px) SORT. Une série alignée qui ne passe pas par
 *     l'origine, ou une courbe qui n'en part pas, en passe à un carreau et demi
 *     au moins (valeur en 0 de la droite, et du premier segment prolongé pour
 *     une courbe). Nouveau tirage borné sinon, repli vérifié.
 *   - AJOUT de la courbe qui part de l'origine (1, 4, 9, 16 : famille
 *     origine_sans_alignement du cadrage).
 *   - Le contexte « nombre de tee-shirts » (grandeur discrète : relier 1 et 2
 *     tee-shirts n'a pas de sens) est remplacé ; « la longueur en fonction de
 *     la masse » aussi (un ressort n'est pas proportionnel). Contextes : prix
 *     et quantité, distance et durée, volume d'eau et durée de remplissage,
 *     prix et longueur de tissu, altitude d'un drone et temps.
 *   - Exclusion : la série de l'exemple du coup de pouce prévu au cadrage,
 *     (2 ; 5), (4 ; 10), (6 ; 15), ne sort jamais.
 *   - Figure : graduations en 11 au lieu de 10, titre de l'axe des ordonnées
 *     rapproché de l'axe, marqueurs distincts par série (rond, carré,
 *     triangle) en plus des couleurs (la paire rouge et vert est celle que
 *     confond un élève daltonien), `alt` neutre, bloc <desc> retiré.
 *   - reconnaitre_qcm_unique GARDÉ : clé « Oui : les points sont alignés avec
 *     l'origine. » (mot du cours) ; les deux autres choix restent fixes et
 *     chacun matérialise une erreur nommée selon la nature du graphique
 *     (aligne_sans_origine, la principale ; origine_sans_alignement ;
 *     croissante_donc_proportionnelle ; origine_mal_lue ; alignement_mal_lu).
 *     Contrat exporté, codes dans _meta.params.distracteurs.
 *   - reconnaitre_qcm_multiple GARDÉ : la consigne annonce le nombre de
 *     réponses (« Une seule… », « Deux… ») ; jamais deux séries qui finissent au
 *     même point, aucun point commun à deux séries, étiquettes loin des points
 *     des autres séries. Choix « Situation A », « B », « C ».
 *   - reconnaitre_tableau_vf RECADRÉ : quatre lignes sur le graphique à trois
 *     situations (plus de lecture de valeurs, qui relève de l'attendu
 *     « fonctions » ; plus de « La droite passe par l'origine » devant des
 *     points non alignés) ; tournures « Les points de la situation A sont
 *     alignés. », « … sont alignés avec l'origine. », « La situation A est une
 *     situation de proportionnalité. » ; jamais les deux dernières sur la même
 *     situation (elles sont équivalentes) ; la ligne « alignés » porte sur une
 *     situation tirée au hasard parmi les trois (réservée aux non
 *     proportionnelles, elle désignait la situation nommée deux fois, dont
 *     l'autre ligne était alors toujours fausse) ; une à trois vraies.
 *   - reconnaitre_classer SORTI : aucun graphique (couples chiffrés = tableau,
 *     geste de 5e).
 *   - reconnaitre_coeff_texte SORTI : calculer le coefficient n'est pas
 *     « reconnaître », et la réponse était la première graduation de l'axe
 *     dans 100 % des tirages.
 *   - Noms de situations A, B, C en lettres droites : ce sont des étiquettes,
 *     pas des points ni des variables (mode auto, À ARBITRER).
 *   - Rappel brouillon : AUCUN (décision : lecture de figure, rien à calculer).
 * Trois formats (avant : cinq).
 */

import { randomChoice, randInt, shuffle, makeQcm, makeVraiFaux } from '../../../utils';
import { svgRepere, imgElement, geometrieRepere, COULEURS_SERIES, FORMES_SERIES } from './proportionnalite';

const ATTENDU_ID = 171;
const L_RECONNAITRE = 'Reconnaître la proportionnalité sur un graphique';
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ─── Contextes : étiquettes d'axes, phrase d'énoncé, mots de l'alt ───
const SITUATIONS = [
    { x: 'quantité (kg)', y: 'prix (€)', phrase: 'le prix payé en fonction de la quantité achetée', ax: 'la quantité en kg', ay: 'le prix en euros' },
    { x: 'durée (min)', y: 'distance (km)', phrase: 'la distance parcourue en fonction de la durée du trajet', ax: 'la durée en minutes', ay: 'la distance en km' },
    { x: 'durée (min)', y: 'volume (L)', phrase: 'le volume d\'eau dans une cuve en fonction de la durée de remplissage', ax: 'la durée en minutes', ay: 'le volume en litres' },
    { x: 'longueur (m)', y: 'prix (€)', phrase: 'le prix payé en fonction de la longueur de tissu achetée', ax: 'la longueur en mètres', ay: 'le prix en euros' },
    { x: 'temps (s)', y: 'altitude (m)', phrase: 'l\'altitude d\'un drone en fonction du temps écoulé', ax: 'le temps en secondes', ay: 'l\'altitude en mètres' },
];

// ─── Séries : ordonnées en CENTIÈMES entiers (vérification exacte) ───
const XSETS = [
    [2, 4, 6, 8], [1, 3, 5, 7], [3, 5, 7, 9], [2, 4, 7, 9], [1, 4, 6, 9], [2, 5, 7, 10],
    [4, 6, 8, 10], [1, 3, 6, 8], [2, 4, 6, 10], [3, 5, 8, 10], [1, 3, 5, 8], [2, 3, 6, 9],
];
const K_PROP = [80, 120, 150, 200, 250];     // coefficient × 100
const A_AFFINE = [80, 120, 150, 200];         // pente × 100
const B_AFFINE = [-4, -3, -2, 2, 3, 4, 5, 6]; // ordonnée en 0, en unités
const C_COURBE = [20, 25, 30];                // y = c·x², c × 100
const B_COURBE = [-3, -2, 4, 5, 6, 7];        // décalage de la courbe qui ne part pas de O
const Y_MIN = 100;                            // 1 unité
const Y_MAX = 2400;                           // 24 unités

const FABRIQUES = {
    prop: () => {
        const k = randomChoice(K_PROP);
        const xs = randomChoice(XSETS);
        return { nature: 'prop', xs, ys: xs.map((x) => k * x), params: { k } };
    },
    affine: () => {
        const a = randomChoice(A_AFFINE);
        const b = randomChoice(B_AFFINE);
        const xs = randomChoice(XSETS);
        return { nature: 'affine', xs, ys: xs.map((x) => a * x + 100 * b), params: { a, b } };
    },
    courbe_origine: () => {
        const c = randomChoice(C_COURBE);
        const xs = randomChoice(XSETS);
        return { nature: 'courbe_origine', xs, ys: xs.map((x) => c * x * x), params: { c } };
    },
    courbe: () => {
        const c = randomChoice([25, 30]);
        const b = randomChoice(B_COURBE);
        const xs = randomChoice(XSETS);
        return { nature: 'courbe', xs, ys: xs.map((x) => c * x * x + 100 * b), params: { c, b } };
    },
};

// ─── Second chemin : nature relue sur les points (produits en croix entiers) ───
const aligne = (xs, ys) => xs.every((x, i) => (x - xs[0]) * (ys[1] - ys[0]) === (ys[i] - ys[0]) * (xs[1] - xs[0]));
const parOrigine = (xs, ys) => aligne(xs, ys) && xs[0] * ys[1] === xs[1] * ys[0];
const natureRelue = (s) => {
    if (parOrigine(s.xs, s.ys)) return 'prop';
    if (aligne(s.xs, s.ys)) return 'affine';
    // non alignée : part-elle de l'origine ? (y = c·x², toutes les abscisses)
    return s.xs.every((x, i) => s.ys[i] * s.xs[0] * s.xs[0] === s.ys[0] * x * x) ? 'courbe_origine' : 'courbe';
};

/** Écart vertical maximal d'un point à la corde (premier, dernier point), en centièmes. */
const ecartCorde = (xs, ys) => {
    const n = xs.length - 1;
    let max = 0;
    for (let i = 1; i < n; i++) {
        const surCorde = ys[0] + (ys[n] - ys[0]) * (xs[i] - xs[0]) / (xs[n] - xs[0]);
        max = Math.max(max, Math.abs(ys[i] - surCorde));
    }
    return max;
};
/** Valeur en 0 de la droite qui passe par les deux premiers points, en centièmes. */
const origineDuPremierSegment = (xs, ys) => ys[0] - xs[0] * (ys[1] - ys[0]) / (xs[1] - xs[0]);

/** Pas de graduation et hauteur du repère pour un maximum donné (en centièmes). */
const echelleY = (maxCentiemes) => {
    const m = maxCentiemes / 100;
    const yStep = m <= 10.5 ? 1 : 2;
    return { yStep, ymax: yStep * Math.ceil(m / yStep + 0.3) };
};

// Exemple du coup de pouce prévu par le cadrage : ne sort jamais.
const estExempleCoupDePouce = (s) => s.nature === 'prop' && s.params.k === 250
    && [2, 4, 6].every((x) => s.xs.includes(x));

/** Une série est-elle valide, lisible et de la nature annoncée, pour ce pas ? */
const serieValide = (s, yStep) => {
    if (natureRelue(s) !== s.nature) throw new Error(`gen_171 : nature ${s.nature} non vérifiée (${s.ys.join(',')})`);
    if (s.ys.some((y) => y < Y_MIN || y > Y_MAX)) return false;
    if (estExempleCoupDePouce(s)) return false;
    const carreau = 100 * yStep;
    if (s.nature === 'courbe_origine' || s.nature === 'courbe') {
        if (ecartCorde(s.xs, s.ys) < carreau) return false;          // au moins un carreau d'écart
    }
    if (s.nature === 'affine' && Math.abs(100 * s.params.b) < 1.5 * carreau) return false;
    if (s.nature === 'courbe') {
        if (Math.abs(100 * s.params.b) < 1.5 * carreau) return false;
        if (Math.abs(origineDuPremierSegment(s.xs, s.ys)) < 1.5 * carreau) return false;
    }
    return true;
};

const REPERE_X = { xmax: 10, xStep: 1 };

/** Contrôles à l'échelle du dessin (pixels) pour plusieurs séries. */
const lisibleEnPixels = (series, yStep, ymax) => {
    const G = geometrieRepere({ ...REPERE_X, ymax, yStep });
    const pts = series.map((s) => s.xs.map((x, i) => [G.X(x), G.Y(s.ys[i] / 100)]));
    // aucun point d'une série à moins de 10 px d'un point d'une autre série
    for (let a = 0; a < pts.length; a++) {
        for (let b = a + 1; b < pts.length; b++) {
            if (pts[a].some((p) => pts[b].some((q) => Math.hypot(p[0] - q[0], p[1] - q[1]) < 10))) return false;
        }
    }
    // aucun point posé sur le tracé d'une autre série (moins de 6 px d'un de ses segments)
    const distSegment = (p, u, v) => {
        const dx = v[0] - u[0], dy = v[1] - u[1];
        const t = Math.max(0, Math.min(1, ((p[0] - u[0]) * dx + (p[1] - u[1]) * dy) / (dx * dx + dy * dy)));
        return Math.hypot(p[0] - (u[0] + t * dx), p[1] - (u[1] + t * dy));
    };
    for (let a = 0; a < pts.length; a++) {
        for (let b = 0; b < pts.length; b++) {
            if (a === b) continue;
            for (let j = 0; j + 1 < pts[b].length; j++) {
                if (pts[a].some((p) => distSegment(p, pts[b][j], pts[b][j + 1]) < 6)) return false;
            }
        }
    }
    // étiquette à droite du dernier point : loin des points des autres séries,
    // et les derniers points assez écartés pour que deux étiquettes ne se touchent pas
    const fins = pts.map((l) => l[l.length - 1]);
    for (let a = 0; a < fins.length; a++) {
        const boite = { x0: fins[a][0] + 5, x1: fins[a][0] + 20, y0: fins[a][1] - 9, y1: fins[a][1] + 9 };
        for (let b = 0; b < pts.length; b++) {
            if (b === a) continue;
            if (Math.hypot(fins[a][0] - fins[b][0], fins[a][1] - fins[b][1]) < 22) return false;
            if (pts[b].some((q) => q[0] > boite.x0 - 5 && q[0] < boite.x1 + 5 && q[1] > boite.y0 - 5 && q[1] < boite.y1 + 5)) return false;
            // ni sur le tracé d'une autre série (segment échantillonné au pixel)
            for (let j = 0; j + 1 < pts[b].length; j++) {
                const [u, v] = [pts[b][j], pts[b][j + 1]];
                const n = Math.ceil(Math.hypot(v[0] - u[0], v[1] - u[1]));
                for (let t = 0; t <= n; t++) {
                    const x = u[0] + (v[0] - u[0]) * t / n, y = u[1] + (v[1] - u[1]) * t / n;
                    if (x > boite.x0 - 2 && x < boite.x1 + 2 && y > boite.y0 - 2 && y < boite.y1 + 2) return false;
                }
            }
        }
    }
    return true;
};

/** Une série, pour le QCM à réponse unique. */
const tirerUneSerie = (nature) => {
    for (let essai = 0; essai < 200; essai++) {
        const s = FABRIQUES[nature]();
        const { yStep, ymax } = echelleY(Math.max(...s.ys));
        if (serieValide(s, yStep)) return { s, yStep, ymax };
    }
    // Repli vérifié par nature (mêmes contrôles, throw s'il échoue)
    const REPLIS = {
        prop: { nature: 'prop', xs: [2, 4, 6, 8], ys: [240, 480, 720, 960], params: { k: 120 } },
        affine: { nature: 'affine', xs: [1, 3, 5, 7], ys: [480, 640, 800, 960], params: { a: 80, b: 4 } },
        courbe_origine: { nature: 'courbe_origine', xs: [2, 4, 6, 8], ys: [100, 400, 900, 1600], params: { c: 25 } },
        courbe: { nature: 'courbe', xs: [1, 3, 5, 7], ys: [525, 725, 1125, 1725], params: { c: 25, b: 5 } },
    };
    const s = REPLIS[nature];
    const { yStep, ymax } = echelleY(Math.max(...s.ys));
    if (!serieValide(s, yStep)) throw new Error(`gen_171 : repli ${nature} invalide`);
    return { s, yStep, ymax, repli: true };
};

/**
 * Trois situations A, B, C : une ou deux proportionnelles, natures variées.
 * Graphique à pas et hauteur FIXES (pas de 2, jusqu'à 24) : chaque série est
 * alors lisible pour elle-même, et le graphique se construit série par série
 * (chacune re-tirée, bornée, tant qu'elle gêne les précédentes). Les NATURES
 * sont tirées d'abord et gardées : sinon le rejet, plus fréquent pour les
 * courbes, ferait pencher le tirage vers deux situations proportionnelles.
 */
const TROIS = { yStep: 2, ymax: 24 };
// Repli vérifié (mêmes contrôles au moment du repli, throw sinon) : un tirage réel
// du générateur, proportionnelle 1,2 / alignée 1,5x + 6 / courbe 0,25x².
const REPLI_TROIS = [
    { nature: 'prop', xs: [1, 3, 6, 8], ys: [120, 360, 720, 960], params: { k: 120 } },
    { nature: 'affine', xs: [3, 5, 8, 10], ys: [1050, 1350, 1800, 2100], params: { a: 150, b: 6 } },
    { nature: 'courbe_origine', xs: [2, 4, 6, 8], ys: [100, 400, 900, 1600], params: { c: 25 } },
];
const tirerTroisSituations = () => {
    const nbProp = randInt(1, 2);
    const autres = shuffle(['affine', 'courbe_origine', 'courbe']).slice(0, 3 - nbProp);
    const natures = shuffle([...Array(nbProp).fill('prop'), ...autres]);
    for (let tour = 0; tour < 80; tour++) {
        const series = [];
        for (const n of natures) {
            let trouvee = null;
            for (let essai = 0; essai < 120 && !trouvee; essai++) {
                const s = FABRIQUES[n]();
                if (!serieValide(s, TROIS.yStep)) continue;
                if (n === 'prop' && series.some((t) => t.nature === 'prop' && t.params.k === s.params.k)) continue;
                if (!lisibleEnPixels([...series, s], TROIS.yStep, TROIS.ymax)) continue;
                trouvee = s;
            }
            if (!trouvee) break;
            series.push(trouvee);
        }
        // un graphique qui monte au moins aux deux tiers de la hauteur
        if (series.length === 3 && Math.max(...series.flatMap((s) => s.ys)) >= 1600) return { series, ...TROIS };
    }
    const series = REPLI_TROIS.map((s) => ({ ...s }));
    if (!series.every((s) => serieValide(s, TROIS.yStep)) || !lisibleEnPixels(series, TROIS.yStep, TROIS.ymax)) {
        throw new Error('gen_171 : repli trois situations invalide');
    }
    return { series, ...TROIS, repli: true };
};

const pointsDe = (s) => s.xs.map((x, i) => [x, s.ys[i] / 100]);
const LABELS = ['A', 'B', 'C'];

const figureUne = (sit, s, yStep, ymax) => imgElement(
    svgRepere([{ points: pointsDe(s), couleur: COULEURS_SERIES[0], forme: FORMES_SERIES[0] }],
        { ...REPERE_X, ymax, yStep, xLabel: sit.x, yLabel: sit.y }),
    'medium',
    `Graphique : quatre points reliés par des segments, ${sit.ax} en abscisse, de 0 à 10, ${sit.ay} en ordonnée, de 0 à ${ymax}.`,
);

const figureTrois = (sit, series, yStep, ymax) => imgElement(
    svgRepere(series.map((s, i) => ({ label: LABELS[i], points: pointsDe(s), couleur: COULEURS_SERIES[i], forme: FORMES_SERIES[i] })),
        { ...REPERE_X, ymax, yStep, xLabel: sit.x, yLabel: sit.y }),
    'medium',
    `Graphique : trois séries de quatre points reliés par des segments, nommées A (ronds bleus), B (carrés rouges) et C (triangles verts), ${sit.ax} en abscisse, de 0 à 10, ${sit.ay} en ordonnée, de 0 à ${ymax}.`,
);

// ═══════════════════════════════════════════════════════════
// Contrat des distracteurs (donnée pure, jamais lue à l'exécution)
// ═══════════════════════════════════════════════════════════

const C_OUI = 'Oui : les points sont alignés avec l\'origine.';
const C_NON_ORIGINE = 'Non : les points sont alignés, mais pas avec l\'origine.';
const C_NON_ALIGNES = 'Non : les points ne sont pas alignés.';

// Pour chaque nature : la clé, et le code d'erreur de chacun des deux autres choix.
const CHOIX_PAR_NATURE = {
    prop: { cle: C_OUI, erreurs: { [C_NON_ORIGINE]: 'origine_mal_lue', [C_NON_ALIGNES]: 'alignement_mal_lu' } },
    affine: { cle: C_NON_ORIGINE, erreurs: { [C_OUI]: 'aligne_sans_origine', [C_NON_ALIGNES]: 'alignement_mal_lu' } },
    courbe_origine: { cle: C_NON_ALIGNES, erreurs: { [C_OUI]: 'origine_sans_alignement', [C_NON_ORIGINE]: 'alignement_mal_lu' } },
    courbe: { cle: C_NON_ALIGNES, erreurs: { [C_OUI]: 'croissante_donc_proportionnelle', [C_NON_ORIGINE]: 'alignement_mal_lu' } },
};

export const REGLES_DISTRACTEURS_QCM = {
    reconnaitre_qcm_unique: {
        support: 'une série de quatre points (abscisses de 1 à 10, ordonnées de 1 à 24), nature tirée : proportionnelle 1/3, alignée sans l\'origine 1/3, courbe qui part de l\'origine 1/6, courbe qui n\'en part pas 1/6 ; trois choix FIXES, la clé dépend de la nature',
        familles: {
            aligne_sans_origine: '« Oui » devant des points alignés dont la droite ne passe pas par l\'origine : l\'élève s\'arrête à l\'alignement (erreur principale du cadrage)',
            origine_sans_alignement: '« Oui » devant des points NON alignés sur une courbe qui part de l\'origine (y = c·x²) : l\'élève s\'arrête à l\'origine',
            croissante_donc_proportionnelle: '« Oui » devant des points non alignés, croissants, dont la courbe ne passe pas par l\'origine : « ça augmente, donc c\'est proportionnel »',
            origine_mal_lue: '« Non : alignés, mais pas avec l\'origine » devant une situation de proportionnalité : le trait s\'arrête au premier point, l\'élève ne le prolonge pas jusqu\'à l\'origine',
            alignement_mal_lu: '« Non : pas alignés » devant des points alignés, ou « Non : alignés, mais pas avec l\'origine » devant des points qui ne le sont pas',
        },
        supprimees: {},
        garantie: 'la nature déclarée par la fabrique est revérifiée par produits en croix entiers (ordonnées en centièmes), throw sinon ; une série non alignée s\'écarte de sa corde d\'un carreau au moins ; une droite ou une courbe qui ne part pas de l\'origine en passe à un carreau et demi au moins ; les trois choix sont toujours présents, la clé se déplace avec la nature',
    },
    reconnaitre_qcm_multiple: {
        support: 'trois situations A, B, C sur le même graphique, une ou deux proportionnelles (coefficients différents), les autres de natures différentes ; la consigne annonce le nombre de réponses',
        familles: {
            aligne_sans_origine: 'une situation alignée qui ne passe pas par l\'origine',
            origine_sans_alignement: 'une courbe qui part de l\'origine',
            croissante_donc_proportionnelle: 'une courbe croissante qui ne passe pas par l\'origine',
        },
        supprimees: {},
        garantie: 'mêmes contrôles de nature et de lisibilité que le QCM unique, au pas commun ; aucun point à moins de 10 px d\'un point d\'une autre série ni à moins de 6 px de son tracé, derniers points à 22 px au moins les uns des autres, aucune étiquette sur un point d\'une autre série',
    },
};

// ═══════════════════════════════════════════════════════════
// 1. QCM — cette situation est-elle une situation de proportionnalité ?
// ═══════════════════════════════════════════════════════════

const tirerNatureUnique = () => {
    const t = randInt(0, 5);
    if (t < 2) return 'prop';
    if (t < 4) return 'affine';
    return t === 4 ? 'courbe_origine' : 'courbe';
};

const genReconnaitreQcm = () => {
    const sit = randomChoice(SITUATIONS);
    const nature = tirerNatureUnique();
    const { s, yStep, ymax, repli = false } = tirerUneSerie(nature);
    const { cle, erreurs } = CHOIX_PAR_NATURE[natureRelue(s)];
    const payload = makeQcm(
        [C_OUI, C_NON_ORIGINE, C_NON_ALIGNES],
        cle,
        `<p>Le graphique ci-dessous représente ${sit.phrase}.</p>`
        + '<p><b>Est-ce une situation de proportionnalité ?</b></p>',
        { ...opts(L_RECONNAITRE), elements_additionnels: figureUne(sit, s, yStep, ymax) },
    );
    payload._meta.params = { nature, xs: s.xs, ys: s.ys, yStep, repli, distracteurs: Object.values(erreurs) };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// 2. QCM MULTIPLE — quelles situations sont des situations de proportionnalité ?
// ═══════════════════════════════════════════════════════════

const CODE_NON_PROP = { affine: 'aligne_sans_origine', courbe_origine: 'origine_sans_alignement', courbe: 'croissante_donc_proportionnelle' };

const genReconnaitreQcmMultiple = () => {
    const sit = randomChoice(SITUATIONS);
    const { series, yStep, ymax, repli = false } = tirerTroisSituations();
    const choix = LABELS.map((l) => `Situation ${l}`);
    const correct = series.map((s, i) => (natureRelue(s) === 'prop' ? choix[i] : null)).filter(Boolean);
    const annonce = correct.length === 1
        ? 'Une seule de ces trois situations est une situation de proportionnalité. Sélectionne-la.'
        : 'Deux de ces trois situations sont des situations de proportionnalité. Sélectionne-les.';
    const payload = makeQcm(
        choix,
        correct,
        `<p>Le graphique ci-dessous représente trois situations A, B et C : ${sit.phrase}.</p>`
        + `<p><b>${annonce}</b></p>`,
        { ...opts(L_RECONNAITRE), elements_additionnels: figureTrois(sit, series, yStep, ymax) },
        true,
    );
    payload._meta.params = {
        natures: series.map((s) => s.nature), series: series.map((s) => ({ xs: s.xs, ys: s.ys })), yStep, repli,
        distracteurs: series.filter((s) => s.nature !== 'prop').map((s) => CODE_NON_PROP[s.nature]),
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// 3. VRAI/FAUX — quatre affirmations sur le graphique à trois situations
// ═══════════════════════════════════════════════════════════

const AFFIRMATIONS = {
    alignes: (l) => `Les points de la situation ${l} sont alignés.`,
    origine: (l) => `Les points de la situation ${l} sont alignés avec l'origine.`,
    proportionnalite: (l) => `La situation ${l} est une situation de proportionnalité.`,
};
const verdict = (type, nature) => {
    if (type === 'alignes') return nature === 'prop' || nature === 'affine' ? 0 : 1;
    return nature === 'prop' ? 0 : 1;                  // origine, proportionnalité
};

const genReconnaitreVF = () => {
    const sit = randomChoice(SITUATIONS);
    const { series, yStep, ymax, repli = false } = tirerTroisSituations();
    // Une ligne « origine » ou « proportionnalité » par situation (jamais les
    // deux : elles sont équivalentes), plus une ligne « alignés ».
    const lignes = series.map((s, i) => {
        const type = randomChoice(['origine', 'proportionnalite']);
        return { t: AFFIRMATIONS[type](LABELS[i]), v: verdict(type, natureRelue(s)) };
    });
    // La ligne « alignés » va sur l'une des trois situations, tirée au hasard :
    // la situation nommée deux fois est proportionnelle une fois sur deux, et sa
    // seconde ligne ne se devine pas sans la figure. Réservée aux situations non
    // proportionnelles, elle désignait une situation dont l'autre ligne était
    // toujours fausse (100 % des tirages).
    const iAlign = randInt(0, 2);
    lignes.push({ t: AFFIRMATIONS.alignes(LABELS[iAlign]), v: verdict('alignes', natureRelue(series[iAlign])) });
    const nbVraies = lignes.filter((l) => l.v === 0).length;
    if (nbVraies === 0 || nbVraies === lignes.length) throw new Error('gen_171 : V/F sans vraie ou sans fausse');
    const melange = shuffle(lignes);
    const payload = makeVraiFaux(
        melange.map((l) => l.t),
        melange.map((l) => l.v),
        `<p>Le graphique ci-dessous représente trois situations A, B et C : ${sit.phrase}.</p>`
        + '<p><b>Pour chaque affirmation, dis si elle est vraie ou fausse.</b></p>',
        { ...opts(L_RECONNAITRE), elements_additionnels: figureTrois(sit, series, yStep, ymax) },
    );
    payload._meta.params = { natures: series.map((s) => s.nature), yStep, nbVraies, repli };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRE
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    reconnaitre_qcm_unique: genReconnaitreQcm,
    reconnaitre_qcm_multiple: genReconnaitreQcmMultiple,
    reconnaitre_tableau_vf: genReconnaitreVF,
};

export const CATEGORIES = [
    {
        id: 'reconnaitre',
        label: L_RECONNAITRE,
        formatsList: [
            { key: 'reconnaitre_qcm_unique', label: 'QCM' },
            { key: 'reconnaitre_qcm_multiple', label: 'QCM multiple' },
            { key: 'reconnaitre_tableau_vf', label: 'Vrai/Faux' },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
